import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { replaceProfessionalType } from '@/utils/helpers.js';

import useScrollToTop from '../hooks/useScrollToTop.js';
import { useColorStyles } from './Common/BasicStyles.js';
import ToolTipBox from './Common/ToolTipBox.jsx';
import FilledButton from './FilledButton.jsx';
import OutlinedButton from './OutlinedButton.jsx';

const useStyles = makeStyles((theme) => ({
    heading: {
        ...theme.typography.HL1,
    },
    contentText: {
        ...theme.typography.HL5,
    },
    accordionText: {
        fontSize: '1rem',
        textTransform: 'none',
        color: `#2e2e2e`,
    },
    mainCenterCircle: {
        display: 'grid',
        height: ({ isInBookingModal, isInAccordion }) => (isInBookingModal || isInAccordion ? 'auto' : '330px'),
        justifyContent: 'center',
        alignItems: 'center',
        alignContent: 'center',
        padding: ({ isInAccordion }) => (isInAccordion ? '1rem' : 0),
        overflow: ({ isInBookingModal }) => (isInBookingModal ? 'hidden' : 'visible'),
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
            gridTemplateColumns: '1fr',
        },
    },
    mainButtonsDiv: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 10,
        flexDirection: 'row',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    buttonWrapper: {
        margin: 10,
        [theme.breakpoints.down('sm')]: {
            margin: '5px 0',
            width: '100%',
        },
    },
}));

const PatientTypeSelection = ({
    doctor,
    setPatientType,
    mainData,
    loading,
    isInBookingModal = false,
    isInAccordion = false,
    promptOverride,
}) => {
    const classes = useStyles({ isInBookingModal, isInAccordion });
    const { i18n } = useTranslation();
    const colorStyle = useColorStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useScrollToTop();

    if (loading) return null;

    return (
        <div className={classes.mainCenterCircle}>
            <Typography
                className={clsx(
                    isInAccordion ? classes.accordionText : classes.contentText,
                    !isInAccordion && colorStyle.mt5
                )}
            >
                {promptOverride ?? replaceProfessionalType({ text: mainData.heading[0].text, language: i18n.language })}
            </Typography>
            <div className={classes.mainButtonsDiv}>
                <ToolTipBox
                    text={
                        doctor?.allow_new_patients === false || !doctor?.is_for_new_patients
                            ? mainData?.infotip_for_new_patient_cta_disabled?.[0]?.text
                            : ''
                    }
                >
                    <div className={classes.buttonWrapper}>
                        <FilledButton
                            disabled={!doctor?.is_for_new_patients}
                            fullWidth={isMobile}
                            text={mainData?.new_patient_button[0].text}
                            onPress={() => setPatientType(false)}
                            dataTestId="booking-new-patient-btn"
                        />
                    </div>
                </ToolTipBox>
                <ToolTipBox
                    text={
                        !doctor?.is_for_returning_patients
                            ? mainData?.infotip_for_returning_patient_cta_disabled?.[0]?.text
                            : ''
                    }
                >
                    <div className={classes.buttonWrapper}>
                        <OutlinedButton
                            disabled={!doctor?.is_for_returning_patients}
                            fullWidth={isMobile}
                            text={mainData?.current_patient_button[0].text}
                            onPress={() => setPatientType(true)}
                            dataTestId="booking-returning-patient-btn"
                        />
                    </div>
                </ToolTipBox>
            </div>
        </div>
    );
};

export default PatientTypeSelection;
